import React, { ReactNode } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";

import {
  DinarReceiptIcon,
  HandPropertiesIcon,
  HouseIcon,
  Typography,
} from "@/components/atoms";
import {
  useIsAdhaJourney,
  useTranslation,
  useUIColors,
  useWebViewport,
} from "@/core/hooks";
import {
  WEB_PAGE_CONTENT_PADDING,
  WEB_SHELL_PADDING_DESKTOP,
  WEB_SHELL_PADDING_MOBILE,
} from "@/core/constants/webLayout";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { BREAKPOINTS } from "@/core/utils/responsive";
import { useRouter } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { WebHeader, type BreadcrumbItem } from "./WebHeader";

type WebTabId = "home" | "properties" | "payments" | null;

export interface WebAppLayoutProps {
  /** Which main tab should be highlighted in the sidebar on web. */
  activeTab: WebTabId;
  children: ReactNode;
  /** Header title */
  headerTitle?: string;
  /** Optional breadcrumbs for the header */
  breadcrumbs?: BreadcrumbItem[];
  /** Whether to show back button in header */
  showBackButton?: boolean;
  /** Callback for back button press */
  onBackPress?: () => void;
  /** Whether to hide the left sidebar (e.g. property catalogue pages) */
  hideSidebar?: boolean;
}

/**
 * Shared web app shell with large Bloom logo header and left sidebar navigation.
 * On mobile (including mobile web), this is a no-op wrapper and simply renders children.
 * On desktop web, it renders:
 * - A header with large logo + notification + profile chip (using WebHeader component)
 * - A left sidebar with Home / My properties / Payments navigation
 *   (hidden when ADHA journey or hideSidebar)
 * - A content area for the page-specific layout
 */
export default function WebAppLayout({
  activeTab = "home",
  children,
  headerTitle,
  breadcrumbs,
  showBackButton = false,
  onBackPress,
  hideSidebar = false,
}: WebAppLayoutProps) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { breakpointWidth } = useWebViewport();
  const isMobile = breakpointWidth < BREAKPOINTS.SHELL;
  const isAdhaJourney = useIsAdhaJourney();

  // On native platforms we don't render the web shell; just return children.
  if (Platform.OS !== "web") {
    return <>{children}</>;
  }

  const handleNavigate = (tab: Exclude<WebTabId, null>) => {
    if (tab === "home") {
      router.push("/(auth)/(home)");
    } else if (tab === "properties") {
      router.push("/(auth)/(home)/properties");
    } else if (tab === "payments") {
      router.push("/(auth)/(home)/payments");
    }
  };

  const getDefaultTitle = (): string => {
    if (activeTab === "home") {
      return t("common.home");
    }
    if (activeTab === "properties") {
      return t("properties.myProperties");
    }
    return t("payments.title");
  };

  const showSidebar = !isMobile && !isAdhaJourney && !hideSidebar;
  // Same three items as the left sidebar, pinned to the bottom once the
  // sidebar hides. Covers stack screens (property payments) as well as tabs.
  const showBottomNav = isMobile && !isAdhaJourney && !hideSidebar;
  const title = headerTitle ?? getDefaultTitle();

  return (
    <View
      style={[
        styles.container,
        // Desktop fills the viewport. Compact web sits above the bottom tab
        // bar — height 100% here pushes that bar outside the clipped root.
        !isMobile && styles.containerFill,
        { backgroundColor: colors.backgroundLevel2 },
      ]}
    >
      {/* Header - Independent, full width at top */}
      <View style={styles.headerWrapper}>
        <WebHeader
          title={title}
          {...(breadcrumbs !== undefined ? { breadcrumbs } : {})}
          showBackButton={showBackButton}
          {...(onBackPress && { onBackPress })}
          alignWithPageContent={isAdhaJourney || hideSidebar}
        />
      </View>

      {/* Root L-shaped layout: [Sidebar] [Content Area] */}
      {/* Keep child order; parent direction handles RTL swap (sidebar ends up on right) */}
      {/* Desktop keeps 32px side padding even when sidebar is hidden */}
      <View
        style={[
          styles.shell,
          isMobile && styles.shellMobile,
          // Normal dashboard (sidebar): space under logo/profile — not flush to header.
          // ADHA / hideSidebar pages manage their own top inset.
          showSidebar && styles.shellBelowHeaderGap,
        ]}
      >
        {showSidebar && (
          <View
            style={[
              styles.sidebar,
              { backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            <SidebarItem
              label={t("home.tabLabel")}
              icon={
                <HouseIcon
                  size={36}
                  color={
                    activeTab === "home"
                      ? colors.buttonDefault
                      : colors.fontSecondary
                  }
                />
              }
              isActive={activeTab === "home"}
              onPress={() => handleNavigate("home")}
            />
            <SidebarItem
              label={t("properties.tabLabel")}
              icon={
                <HandPropertiesIcon
                  size={36}
                  color={
                    activeTab === "properties"
                      ? colors.buttonDefault
                      : colors.fontSecondary
                  }
                />
              }
              isActive={activeTab === "properties"}
              onPress={() => handleNavigate("properties")}
            />
            <SidebarItem
              label={t("payments.tabLabel")}
              icon={
                <DinarReceiptIcon
                  size={36}
                  color={
                    activeTab === "payments"
                      ? colors.buttonDefault
                      : colors.fontSecondary
                  }
                />
              }
              isActive={activeTab === "payments"}
              onPress={() => handleNavigate("payments")}
            />
          </View>
        )}
        <View style={styles.contentArea}>{children}</View>
      </View>
      {showBottomNav && (
        <View
          style={[
            styles.bottomNav,
            {
              backgroundColor: colors.backgroundLevel1,
              paddingBottom: Math.max(insets.bottom, 8),
            },
          ]}
        >
          <SidebarItem
            label={t("home.tabLabel")}
            icon={
              <HouseIcon
                size={24}
                color={
                  activeTab === "home"
                    ? colors.buttonDefault
                    : colors.buttonDisabled
                }
              />
            }
            isActive={activeTab === "home"}
            onPress={() => handleNavigate("home")}
            layout="row"
          />
          <SidebarItem
            label={t("properties.tabLabel")}
            icon={
              <HandPropertiesIcon
                size={24}
                color={
                  activeTab === "properties"
                    ? colors.buttonDefault
                    : colors.buttonDisabled
                }
              />
            }
            isActive={activeTab === "properties"}
            onPress={() => handleNavigate("properties")}
            layout="row"
          />
          <SidebarItem
            label={t("payments.tabLabel")}
            icon={
              <DinarReceiptIcon
                size={24}
                color={
                  activeTab === "payments"
                    ? colors.buttonDefault
                    : colors.buttonDisabled
                }
              />
            }
            isActive={activeTab === "payments"}
            onPress={() => handleNavigate("payments")}
            layout="row"
          />
        </View>
      )}
    </View>
  );
}

interface SidebarItemProps {
  label: string;
  icon: React.ReactNode;
  isActive?: boolean;
  onPress: () => void;
  /** Column for the left sidebar, row item for the compact bottom bar. */
  layout?: "column" | "row";
}

function SidebarItem({
  label,
  icon,
  isActive,
  onPress,
  layout = "column",
}: Readonly<SidebarItemProps>) {
  const colors = useUIColors();
  const isBottom = layout === "row";

  return (
    <Pressable
      onPress={onPress}
      style={[
        isBottom ? styles.bottomNavItem : styles.navItem,
        !isBottom && isActive && { backgroundColor: colors.backgroundLevel2 },
      ]}
      accessibilityRole="button"
    >
      {icon}
      <Typography
        variant={isBottom ? "caption" : "bodyLarge"}
        color={isActive ? "primary" : "secondary"}
        style={isBottom && isActive ? { fontWeight: "700" } : undefined}
      >
        {label}
      </Typography>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
    ...(Platform.OS === "web"
      ? ({ overflow: "hidden" } as object)
      : null),
  },
  containerFill: {
    ...(Platform.OS === "web"
      ? ({ height: "100%", maxHeight: "100%" } as object)
      : null),
  },
  headerWrapper: {
    width: "100%",
    flexShrink: 0,
  },
  shell: {
    flex: 1,
    flexDirection: "row",
    paddingLeft: WEB_SHELL_PADDING_DESKTOP,
    paddingRight: WEB_SHELL_PADDING_DESKTOP,
    paddingTop: 0,
    paddingBottom: 0,
    gap: 24, // Gap between sidebar and content per Figma
    minHeight: 0,
    ...(Platform.OS === "web" ? ({ overflow: "hidden" } as object) : null),
  },
  /** Email / converted-lead dashboard: keep sidebar + content off the header. */
  shellBelowHeaderGap: {
    paddingTop: WEB_PAGE_CONTENT_PADDING,
  },
  shellMobile: {
    paddingLeft: WEB_SHELL_PADDING_MOBILE,
    paddingRight: WEB_SHELL_PADDING_MOBILE,
  },
  sidebar: {
    borderRadius: 12,
    padding: 12,
    gap: 16,
    flexShrink: 0,
    width: 155, // Fixed width to prevent shrinking in RTL
  },
  navItem: {
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  contentArea: {
    flex: 1,
    minHeight: 0,
    ...(Platform.OS === "web" ? ({ overflow: "hidden" } as object) : null),
  },
  bottomNav: {
    flexDirection: "row",
    justifyContent: "space-around",
    flexShrink: 0,
    zIndex: 20,
    width: "100%",
    paddingTop: 8,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: -4 },
  },
  bottomNavItem: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 8,
    gap: 4,
  },
});
